import seajs, { define } from 'seajs-amd'
import _ from 'lodash'
// eslint-disable-next-line import/no-extraneous-dependencies
import * as extension from '@extension'
import PaasSdk from '@mypaas/app-render-engine/src/sdk'
import { isPreview } from './runtime'
import {
    px2rem, refreshDpr, refreshRem, rem2px,
} from './rem'

const { default: Vue } = require('vue')
const { default: Vuex } = require('vuex')
const { default: Axios } = require('axios')
const { default: VueRouter } = require('vue-router')

const PREFIX = 'paas-sdk'
const EXCLUDES = ['http', 'inject', 'utils']
const deps = []

export const defineModule = (id, module) => {
    define(id, () => module)
    deps.push(id)
}

defineModule('vue', Vue)
defineModule('vuex', Vuex)
defineModule('axios', Axios)
defineModule('vue-router', VueRouter)
defineModule('@extension', extension)
defineModule('@mypaas/sdk', PaasSdk)

/**
 * 为了兼容之前类似 `import { axios } from '@platform/http'` 的写法
 *
 * 把每个子 module 单独注册成 amd 模块
 *
 * 否则，只能写成 `const { axios } = require('@platform').http`
 */

function defineSdk(sdk, prefix, excludes = []) {
    Object.keys(sdk).forEach(key => {
        if (excludes.includes(key)) return
        const id = `${prefix}/${key}`
        const val = sdk[key]

        defineModule(id, val)
    })
}

/**
 * inject api 的信息是接口返回的，需要异步注册
 */
function defineInject(sdk, config) {
    const {
        id: appId,
        webapp_env: env,
        engine_version: engine,
        webapp_version: appAssetsVersion,
        apiDomain = '',
        fixedDpr = false,
        buildRem = true,
    } = config

    const inject = {
        ...sdk.inject.default,
        appId,
        env,
        engine,
        appAssetsVersion,
        apiDomain,
        fixedDpr,
        buildRem,
    }
    const id = `${PREFIX}/inject`
    defineModule(id, inject)
    return {
        ...sdk,
        inject: {
            ...inject,
            default: inject,
        },
    }
}

/**
 * http api 依赖 inject api 的数据，所以要异步注册
 */
function defineHttp(instance) {
    const id = `${PREFIX}/http`
    defineModule(id, instance);
    ['axios', 'http'].forEach(item => {
        const key = `$${item}`
        if (!Vue.prototype[key]) {
            Object.defineProperty(Vue.prototype, key, {
                get: () => instance[key] || instance[item],
            })
        }
    })
}

/*
 * utils 下面的 api 有两种引入方式，所以要注册两种形式
 * import cookie from '@platform/utils/cookie'
 * import { cookie } from '@platform/utils'
 */
function defineUtil(sdk, postUnit) {
    // sdk.utils 是一个 getter, 不能直接给属性赋值，所以这里先解构复制再赋值
    const utils = { ...sdk.utils }
    const key = `${PREFIX}/utils`

    utils.postUnit = postUnit
    defineModule(key, utils)
    defineSdk(utils, key)
}

function defineGlobalApp(sdk, requestInstance) {
    const {
        inject: { default: inject },
        invoke: { invoke },
        location: { location },
        auth: { login, logout },
        context: { getContext, setContext, clearContext },
        role: { getRole, setRole, clearRole },
        token: { getToken, setToken, clearToken },
        log: { error, message },
    } = sdk

    const { axios, http } = requestInstance

    window.$app = {
        ...inject,
        invoke,
        login,
        logout,
        getContext,
        setContext,
        clearContext,
        getToken,
        setToken,
        clearToken,
        getRole,
        setRole,
        clearRole,
        error,
        message,
        axios,
        http,
        location,
        refreshDpr,
        refreshRem,
        rem2px,
        px2rem,
        basePath: '/',
        isDesignTime: !isPreview,
    }
}

function defineAsyncSdk(sdk) {
    const test = sdk.utils.postUnit()

    return Promise.all([sdk.http.init(Vue), !(test instanceof Promise) ? Promise.resolve(sdk.utils.postUnit) : sdk.utils.postUnit()]).then(res => {
        defineHttp(res[0])
        defineUtil(sdk, res[1])
        defineGlobalApp(sdk, res[0])
    })
}

function defineModuleLodash() {
    defineModule('@lodash', _)
    Object.keys(_).forEach(id => {
        defineModule(`@lodash/${id}`, _[id])
    })
}

defineModuleLodash() // lodash

function dependencies(config, store, router, SDK, PaasSDK) {
    return new Promise(resolve => {
        Vue.use(SDK, { store, router })
        PaasSDK = defineInject(PaasSDK, config)
        defineSdk(PaasSDK, PREFIX, EXCLUDES)
        if (isPreview) {
            defineModule('paas-sdk/design-components', {})
        }
        defineAsyncSdk(PaasSDK).then(() => {
            seajs.use(deps)
            resolve()
        })
    })
}

export default dependencies
